import React, { useEffect, useRef, Fragment, useState, useCallback } from "react";
import { createPortal } from 'react-dom';
import ProductList from "../productList/ProductList";
import NotFoundProduct from "../notFoundProduct/NotFoundProduct";
import { useLocation, useNavigate } from "react-router-dom";
import useFilters from "../../hooks/useFilters";
import {
  useCategoriesInfiniteQuery,
  useCategorySlugValidQuery,
} from "../../hooks/queries/useCategoriesInfiniteQuery";
import { useProductsInfiniteQuery } from "../../hooks/queries/useProductsInfiniteQuery";
import {
  sanitizeSearchParams,
  searchParamsToString,
} from "../../utils/sanitizeSearchParams";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";

const PortalFilterBadge = ({ label, active, count, children }) => {
  const [buttonRef, setButtonRef] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuWidth, setMenuWidth] = useState(0);

  const updatePosition = useCallback(() => {
    if (buttonRef) {
      const rect = buttonRef.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate available space below and above the button
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Determine if menu should appear below or above the button
      const shouldShowBelow = spaceBelow >= 200 || spaceBelow > spaceAbove;
      
      // Set position relative to viewport
      const top = shouldShowBelow ? rect.bottom + 8 : rect.top - 8;
      
      setMenuPosition({
        top: top,
        left: rect.left,
        position: 'fixed' // Use fixed positioning to stay in place during scroll
      });
      
      setMenuWidth(Math.min(rect.width + 48, 320));
    }
  }, [buttonRef]);

  useEffect(() => {
    if (buttonRef) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      // Add scroll event listener to parent container
      const scrollContainer = buttonRef.closest('.overflow-x-auto');
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', updatePosition);
      }
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', updatePosition);
        }
      };
    }
  }, [buttonRef, updatePosition]);

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <Menu.Button 
            ref={setButtonRef}
            className={`inline-flex items-center px-4 py-2 rounded-full border ${
              active ? 'bg-brand-50 border-brand-500 text-brand-700' : 'bg-white border-gray-300'
            } text-sm font-medium hover:bg-gray-50`}
          >
            {label}
            {count > 0 && (
              <span className="ml-1 text-xs bg-brand-100 text-brand-800 px-1.5 py-0.5 rounded-full">
                {count}
              </span>
            )}
            <ChevronDownIcon className="w-5 h-5 ml-1 -mr-1" aria-hidden="true" />
          </Menu.Button>

          {open && createPortal(
            <Transition
              as={Fragment}
              show={true}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className="fixed z-50 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                style={{
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                  width: `${menuWidth}px`,
                  maxHeight: '80vh',
                  overflowY: 'auto'
                }}
              >
                <div className="py-2">
                  {children}
                </div>
              </Menu.Items>
            </Transition>,
            document.body
          )}
        </>
      )}
    </Menu>
  );
};

const Search = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const category = searchParams.get("Category");
  const query = searchParams.get("query");
  const navigate = useNavigate();

  // Refs
  const buttonRef = useRef(null);
  const productsRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const prevFilters = useRef({});

  // State declarations
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [searchInput, setSearchInput] = useState(query || '');

  const {
    categories,
    isLoading: categoriesLoading,
    error: categoriesError,
    hasNextPage: hasMoreCategories,
    isFetchingNextPage: isLoadingMoreCategories,
    fetchNextPage: fetchMoreCategories,
  } = useCategoriesInfiniteQuery({ assigned: true });

  const { data: categorySlugValid } = useCategorySlugValidQuery(category);

  const handleTouchStart = (e) => {
    setIsDragging(true);
    const touch = e.touches[0];
    setStartX(touch.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    const x = touch.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    setSearchInput(query || '');
  }, [query]);

  const handleSearch = () => {
    const params = new URLSearchParams(location.search);

    if (searchInput.trim()) {
      params.set('query', searchInput.trim());
    } else {
      params.delete('query');
    }

    params.delete('page');
    navigate({ search: searchParamsToString(params) }, { replace: true });
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (!value.trim()) {
      const params = new URLSearchParams(location.search);
      if (!params.has('query')) return;
      params.delete('query');
      params.delete('page');
      navigate({ search: searchParamsToString(params) }, { replace: true });
    }
  };

  // Strip invalid or empty URL params (e.g. legacy category slugs, blank query).
  // Wait for the slug check to resolve — `undefined` means "not answered yet",
  // and treating that as invalid would drop a good slug on first render.
  useEffect(() => {
    if (category && categorySlugValid === undefined) return;

    const { params, changed } = sanitizeSearchParams(location.search, {
      categoryInvalid: Boolean(category) && categorySlugValid === false,
    });
    if (changed) {
      navigate({ search: searchParamsToString(params) }, { replace: true });
    }
  }, [category, categorySlugValid, location.search, navigate]);

  const initialFilters = {
    search: searchParams.get('query') || null,
    category: searchParams.get('Category') || null,
    is_active: searchParams.get('is_active') || null,
    tags: searchParams.get('tags')?.split(',') || null,
    page: parseInt(searchParams.get('page')) || 1,
    size: 12,
    ordering: null
  };

  const { filters, setFilters } = useFilters(initialFilters);

  const {
    data: productsData,
    isLoading: productsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: productsError,
  } = useProductsInfiniteQuery(filters);

  const products =
    productsData?.pages?.flatMap((page) => page.results || []) ?? [];
  const totalCount = productsData?.pages?.[0]?.count ?? 0;
  const loading = productsLoading;
  const error = productsError?.message;
  const hasMore = !!hasNextPage;
  const isLoadingMore = isFetchingNextPage;

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && !loading) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasMore, isLoadingMore, loading]);

  // Set up intersection observer for auto-loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !isLoadingMore) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.5
      }
    );

    const currentButton = buttonRef.current;
    if (currentButton) {
      observer.observe(currentButton);
    }

    return () => {
      if (currentButton) {
        observer.unobserve(currentButton);
      }
    };
  }, [hasMore, loading, isLoadingMore, handleLoadMore]);

  const updateFilter = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
      page: filterName !== 'page' ? 1 : prev.page
    }));
  };

  const categoriesShowMore = hasMoreCategories ? (
    <button
      type="button"
      onClick={() => fetchMoreCategories()}
      disabled={isLoadingMoreCategories}
      className="w-full px-3 py-2 text-sm text-brand-700 rounded-lg border border-dashed border-brand-300 hover:bg-brand-50 disabled:opacity-60"
    >
      {isLoadingMoreCategories ? 'Loading…' : 'Show more categories'}
    </button>
  ) : null;

  const handleCategoryChange = (slug) => {
    const newCategory = filters.category === slug ? null : slug;
    updateFilter('category', newCategory);

    const params = new URLSearchParams(location.search);
    if (newCategory) {
      params.set('Category', newCategory);
    } else {
      params.delete('Category');
    }
    params.delete('page');
    navigate({ search: searchParamsToString(params) }, { replace: true });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    navigate({ search: '' }, { replace: true });
    setFilters({
      search: null,
      category: null,
      is_active: null,
      tags: null,
      page: 1,
      size: 12,
      ordering: null
    });
  };

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      search: query ? `${query}` : null,
      category: category ? category : null,
      page: 1,
    }));
  }, [query, category, setFilters]);

  useEffect(() => {
    if (filters.page === 1) {
      window.scrollTo(0, 0);
    }
  }, [filters.page]);

  useEffect(() => {
    // Only scroll to products section when filters other than page change
    if (productsRef.current && 
      (filters.search !== prevFilters.current?.search ||
       filters.category !== prevFilters.current?.category ||
       filters.tags !== prevFilters.current?.tags ||
       filters.is_active !== prevFilters.current?.is_active ||
       filters.ordering !== prevFilters.current?.ordering)) {
      const yOffset = -100;
      const element = productsRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
    prevFilters.current = {
      search: filters.search,
      category: filters.category,
      tags: filters.tags,
      is_active: filters.is_active,
      ordering: filters.ordering,
    };
  }, [filters.search, filters.category, filters.tags, filters.is_active, filters.ordering]);

  // Update the category menu item rendering
  const renderCategoryMenuItem = (category) => {
    return (
      <Menu.Item key={category.id}>
        <button
          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-gray-50 ${
            filters.category === category.slug
              ? 'bg-brand-50 text-brand-700'
              : 'text-gray-700'
          }`}
          onClick={() => handleCategoryChange(category.slug)}
        >
          <span>{category.name}</span>
        </button>
      </Menu.Item>
    );
  };

  // Update desktop category rendering
  const renderDesktopCategory = (category) => {
    return (
      <button
        key={category.slug}
        onClick={() => handleCategoryChange(category.slug)}
        className={`flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200 ${
          filters.category === category.slug
            ? 'bg-brand-50 border border-brand-500 text-brand-700 shadow-sm'
            : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
        }`}
      >
        <img
          src={category.banner}
          alt={category.name}
          className="h-6 w-6 object-cover rounded-md"
        />
        <span className="ml-3 text-sm font-medium">{category.name}</span>
        {filters.category === category.slug && (
          <svg
            className="ml-auto h-5 w-5 text-brand-600"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
    );
  };

  return (
    <div className="bg-gray-50">
      {/* Fixed Mobile Filters - Show only on mobile and tablet */}
      <div className="lg:hidden fixed top-16 left-0 right-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto relative">
          <div className="flex">
            {/* Scrollable Filter Badges Container */}
            <div 
              ref={scrollContainerRef}
              className="flex-1 overflow-x-auto scrollbar-hide"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ 
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x pinch-zoom'
              }}
            >
              <div className="flex gap-2 items-center px-3 sm:px-4 md:px-6 py-3 min-w-max pr-[120px]">
                {/* Search Badge */}
                {/* <div className="flex-none cursor-grab active:cursor-grabbing">
                  <PortalFilterBadge 
                    label="Search" 
                    active={filters.search}
                    count={filters.search ? 1 : 0}
                  >
                    <div className="p-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchInput}
                          onChange={handleSearchInputChange}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          placeholder="Search products..."
                          className="w-full p-2 border rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                        <button
                          onClick={handleSearch}
                          className="px-3 sm:px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700 flex items-center justify-center transition-colors"
                          aria-label="Search"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </PortalFilterBadge>
                </div> */}

                {/* Categories Badge */}
                <div className="flex-none cursor-grab active:cursor-grabbing">
                  <PortalFilterBadge 
                    label="Categories" 
                    active={filters.category}
                    count={filters.category ? 1 : 0}
                  >
                    <div className="p-2">
                      <div className="mb-8">
                        <h2 className="text-lg mb-2">Categories</h2>
                        <div className="space-y-2 w-full">
                          {categoriesLoading ? (
                            [...Array(5)].map((_, index) => (
                              <div key={index} className="flex items-start px-2 py-3 rounded-md gap-4 bg-gray-100">
                                <div className="h-6 w-6 bg-gray-200 animate-pulse rounded-md"></div>
                                <div className="flex items-center gap-2">
                                  <div className="h-4 w-4 bg-gray-200 animate-pulse rounded"></div>
                                  <div className="h-4 bg-gray-200 animate-pulse rounded w-32"></div>
                                </div>
                              </div>
                            ))
                          ) : categoriesError ? (
                            <div className="text-red-500">{categoriesError.message || 'Error loading categories'}</div>
                          ) : (
                            <>
                              {categories.map((cat) => renderCategoryMenuItem(cat))}
                              {categoriesShowMore}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </PortalFilterBadge>
                </div>

                {/* Tags Badge */}
                {/* <div className="flex-none cursor-grab active:cursor-grabbing">
                  <PortalFilterBadge 
                    label="Tags" 
                    active={filters.tags?.length > 0}
                    count={filters.tags?.length || 0}
                  >
                    <div className="p-2">
                      <div className="mb-8">
                        <h2 className="text-lg mb-2">Product Tags</h2>
                        <div className="space-y-2">
                          {['sale', 'new', 'featured', 'eco-friendly'].map((tag) => (
                            <label key={tag} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={filters.tags?.includes(tag)}
                                onChange={() => handleTagsChange(tag)}
                                className="form-checkbox h-4 w-4 text-brand-600 rounded focus:ring-brand-500"
                              />
                              <span className="text-sm capitalize">{tag}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PortalFilterBadge>
                </div> */}

                {/* Sort Badge */}
                <div className="flex-none cursor-grab active:cursor-grabbing">
                  <PortalFilterBadge 
                    label="Sort" 
                    active={filters.ordering}
                    count={filters.ordering ? 1 : 0}
                  >
                    <div className="p-2">
                      <div className="mb-4">
                        <h2 className="text-lg mb-2">Sort By</h2>
                        <div className="space-y-2">
                          <button
                            onClick={() => updateFilter('ordering', null)}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              !filters.ordering
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Default</span>
                            {!filters.ordering && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => updateFilter('ordering', 'name')}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              filters.ordering === 'name'
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Name (A-Z)</span>
                            {filters.ordering === 'name' && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => updateFilter('ordering', '-name')}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              filters.ordering === '-name'
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Name (Z-A)</span>
                            {filters.ordering === '-name' && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => updateFilter('ordering', 'price')}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              filters.ordering === 'price'
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Price (Low to High)</span>
                            {filters.ordering === 'price' && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => updateFilter('ordering', '-price')}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              filters.ordering === '-price'
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Price (High to Low)</span>
                            {filters.ordering === '-price' && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => updateFilter('ordering', '-created_at')}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              filters.ordering === '-created_at'
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Newest First</span>
                            {filters.ordering === '-created_at' && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => updateFilter('ordering', 'created_at')}
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                              filters.ordering === 'created_at'
                                ? 'bg-brand-50 border border-brand-500 text-brand-700'
                                : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                            }`}
                          >
                            <span className="text-sm">Oldest First</span>
                            {filters.ordering === 'created_at' && (
                              <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </PortalFilterBadge>
                </div>
              </div>
            </div>

            {/* Fixed Clear Filters Button */}
            {(filters.search || filters.category || filters.tags?.length > 0 || filters.ordering) && (
              <div className="flex-none border-l border-gray-100">
                <div className="h-full flex items-center px-3 sm:px-4 md:px-6 py-3 bg-white">
                  <button
                    onClick={handleClearFilters}
                    className="inline-flex items-center px-4 py-2 rounded-full border border-red-300 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 whitespace-nowrap"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div ref={productsRef} className="-mb-6"></div>
      <div className="mx-auto max-w-screen-2xl px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Add padding top on mobile to account for fixed filters */}
        <div className="pt-24 lg:pt-8 xl:pt-10">
          {/* Scroll target with negative margin to pull scroll position up */}


          <div className="flex flex-col lg:flex-row w-full gap-4">
            {/* Desktop Sidebar - Hidden on mobile and tablet */}
            <div className="hidden lg:block lg:w-3/12">
              <div className="bg-white p-4 rounded-lg shadow-sm sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="my-4 mb-8">
                  <div className="mb-4">
                    <h3 className="font-bold mb-2">Search</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Search products..."
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                      <button
                        onClick={handleSearch}
                        className="px-3 sm:px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700 flex items-center justify-center transition-colors"
                        aria-label="Search"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Category Filter */}
                <div className="mb-8">
                  <h2 className="text-lg mb-2">Categories</h2>
                  <div className="space-y-2 w-full">
                    {categoriesLoading ? (
                      [...Array(5)].map((_, index) => (
                        <div key={index} className="flex items-start px-2 py-3 rounded-md gap-4 bg-gray-100">
                          <div className="h-6 w-6 bg-gray-200 animate-pulse rounded-md"></div>
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-4 bg-gray-200 animate-pulse rounded"></div>
                            <div className="h-4 bg-gray-200 animate-pulse rounded w-32"></div>
                          </div>
                        </div>
                      ))
                    ) : categoriesError ? (
                      <div className="text-red-500">{categoriesError.message || 'Error loading categories'}</div>
                    ) : (
                      <>
                        {categories.map(renderDesktopCategory)}
                        {categoriesShowMore}
                      </>
                    )}
                  </div>
                </div>

                {/* Sort Options */}
                <div className="mb-8">
                  <h2 className="text-lg mb-2">Sort By</h2>
                  <div className="space-y-2">
                    <button
                      onClick={() => updateFilter('ordering', null)}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        !filters.ordering
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Default</span>
                      {!filters.ordering && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => updateFilter('ordering', 'name')}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        filters.ordering === 'name'
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Name (A-Z)</span>
                      {filters.ordering === 'name' && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => updateFilter('ordering', '-name')}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        filters.ordering === '-name'
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Name (Z-A)</span>
                      {filters.ordering === '-name' && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => updateFilter('ordering', 'price')}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        filters.ordering === 'price'
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Price (Low to High)</span>
                      {filters.ordering === 'price' && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => updateFilter('ordering', '-price')}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        filters.ordering === '-price'
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Price (High to Low)</span>
                      {filters.ordering === '-price' && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => updateFilter('ordering', '-created_at')}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        filters.ordering === '-created_at'
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Newest First</span>
                      {filters.ordering === '-created_at' && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => updateFilter('ordering', 'created_at')}
                      className={`flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 ${
                        filters.ordering === 'created_at'
                          ? 'bg-brand-50 border border-brand-500 text-brand-700'
                          : 'bg-white border border-gray-200 hover:border-brand-500 hover:bg-brand-50'
                      }`}
                    >
                      <span className="text-sm">Oldest First</span>
                      {filters.ordering === 'created_at' && (
                        <svg className="ml-auto h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={handleClearFilters}
                    className="text-sm bg-red-600 w-full text-white p-2 rounded-md hover:bg-red-500 transition-all ease-in duration-200"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="w-full lg:w-9/12">
              <div className="flex flex-col sm:flex-row justify-between items-center mb-3 bg-orange-100 border border-gray-100 rounded p-3">
                <h6 className="text-sm mb-2 sm:mb-0">
                  Total <span className="font-bold">{totalCount}</span> items Found
                </h6>
              </div>

              {loading && products.length === 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3  gap-4">
                  {[...Array(12)].map((_, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow animate-pulse">
                      <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center text-red-500 mt-8">Error: {error}</div>
              ) : products.length === 0 ? (
                <NotFoundProduct />
              ) : (
                <>
                  <ProductList
                    data={products}
                    isAuthorized={true}
                  />

                  {/* Load More Button */}
                  <div ref={buttonRef} className="flex justify-center mt-8">
                    {hasMore && (
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore || loading}
                        className={`px-6 py-3 rounded-md text-white font-medium transition-all duration-200 ${
                          isLoadingMore || loading
                            ? 'bg-brand-400 cursor-not-allowed'
                            : 'bg-brand-600 hover:bg-brand-700'
                        }`}
                      >
                        {isLoadingMore || loading ? (
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        ) : (
                          'Load More'
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* <div className="mt-6">
                <Pagination
                  currentPage={filters.page}
                  totalCount={data?.count || 0}
                  pageSize={filters.size}
                  onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
                />
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;